import React from 'react';
import ReactECharts from 'echarts-for-react';
import { EnergySummary, GridStatus } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';
import { useTranslation } from 'react-i18next';

interface EnergyDetailViewProps {
  data: EnergySummary;
  gridData: GridStatus;
  energyDataService: EnergyDataService;
}

export const EnergyDetailView: React.FC<EnergyDetailViewProps> = ({ data, gridData, energyDataService }) => {
  const { t } = useTranslation();
  // Get REAL time series data from JSON files - NO FAKE DATA
  const pvTimeSeriesData = energyDataService.getPVTimeSeriesData();
  const consumerTimeSeriesData = energyDataService.getConsumerTimeSeriesData();
  const gridTimeSeriesData = energyDataService.getGridTimeSeriesData();
  
  // Use real data if available, otherwise show current values only
  const hasPVData = pvTimeSeriesData.pv1Data.length > 0;
  const hasConsumerData = consumerTimeSeriesData.consumptionData.length > 0;
  const hasGridData = gridTimeSeriesData.importData.length > 0;
  
  const timeData = hasPVData ? pvTimeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  
  // Use REAL data from JSON files
  const pv1Data = hasPVData ? pvTimeSeriesData.pv1Data : [0, 0, 0, 0, 0, 0, 0];
  const pv2Data = hasPVData ? pvTimeSeriesData.pv2Data : [0, 0, 0, 0, 0, 0, 0];
  const pvProductionData = pv1Data.map((pv1, i) => pv1 + (pv2Data[i] || 0));
  
  const consumptionData = hasConsumerData ? consumerTimeSeriesData.consumptionData : [data.consumptionToday / 24, data.consumptionToday / 24, data.consumptionToday / 24, data.consumptionToday / 24, data.consumptionToday / 24, data.consumptionToday / 24, data.consumptionToday / 24];
  
  const gridImportData = hasGridData ? gridTimeSeriesData.importData : [Math.max(0, gridData.importPower), Math.max(0, gridData.importPower), Math.max(0, gridData.importPower), Math.max(0, gridData.importPower), Math.max(0, gridData.importPower), Math.max(0, gridData.importPower), Math.max(0, gridData.importPower)];
  const gridExportData = hasGridData ? gridTimeSeriesData.exportData : [Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower), Math.max(0, gridData.exportPower)];

  const energyFlowChartOption = {
    title: {
      text: t('charts.energyFlowOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value} kW<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.pvProduction'), t('legends.consumption'), t('legends.gridImport'), t('legends.gridExport')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
    series: [
      {
        name: t('legends.pvProduction'),
        data: pvProductionData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#F59E0B' },
        areaStyle: { color: 'rgba(245, 158, 11, 0.2)' }
      },
      {
        name: t('legends.consumption'),
        data: consumptionData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.2)' }
      },
      {
        name: t('legends.gridImport'),
        data: gridImportData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#3B82F6' }
      },
      {
        name: t('legends.gridExport'),
        data: gridExportData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' }
      }
    ]
  };

  const energyBalanceChartOption = {
    title: {
      text: t('charts.dailyEnergyBalance'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} kWh ({d}%)'
    },
    series: [{
      name: 'Energy Balance',
      type: 'pie',
      radius: '60%',
      data: [
        { value: data.pvProductionToday, name: t('legends.pvProduction'), itemStyle: { color: '#F59E0B' } },
        { value: data.consumptionToday, name: t('legends.consumption'), itemStyle: { color: '#EF4444' } },
        { value: gridData.importEnergy, name: t('legends.gridImport'), itemStyle: { color: '#3B82F6' } },
        { value: gridData.exportEnergy, name: t('legends.gridExport'), itemStyle: { color: '#10B981' } }
      ],
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  };

  const consumptionTrendChartOption = {
    title: {
      text: t('charts.consumptionTrend'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} kWh'
    },
    xAxis: {
      type: 'category',
      data: [t('common.today'), t('common.weekly'), t('common.average')]
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kWh'
      }
    },
    series: [{
      // Show actual consumption data we have available
      data: [
        data.consumptionToday,           // Today's consumption
        data.consumption7Days,           // 7-day total
        data.consumption7Days > 0 ? data.consumption7Days / 7 : 0  // Daily average
      ],
      type: 'bar',
      itemStyle: { color: '#EF4444' }
    }]
  };

  const selfSufficiency = data.pvProductionToday > 0 ? 
    ((data.pvProductionToday - gridData.importEnergy) / data.consumptionToday * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">⚡ {t('energy.title')}</h2>
            <p className="text-gray-600">{t('energy.subtitle')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{selfSufficiency.toFixed(1)}{t('units.percent')}</div>
            <div className="text-sm text-gray-500">{t('energy.selfSufficiency')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={energyFlowChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={energyBalanceChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <ReactECharts option={consumptionTrendChartOption} style={{ height: '300px' }} />
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production & Consumption */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 {t('energy.title')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.pvProduction')}</span>
              <span className="font-semibold text-yellow-600">{data.pvProductionToday} {t('units.kWh')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.consumption')}</span>
              <span className="font-semibold text-red-600">{data.consumptionToday} {t('units.kWh')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.totalConsumption')}</span>
              <span className="font-semibold">{data.consumption7Days} {t('units.kWh')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.averageConsumption')}</span>
              <span className="font-semibold">{(data.consumption7Days / 7).toFixed(1)} {t('units.kWh')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.energySurplus')}</span>
              <span className={`font-semibold ${data.pvProductionToday - data.consumptionToday > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.pvProductionToday - data.consumptionToday > 0 ? '+' : ''}{(data.pvProductionToday - data.consumptionToday).toFixed(1)} {t('units.kWh')}
              </span>
            </div>
          </div>
        </div>

        {/* Grid Interaction */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔌 {t('energy.gridInteraction')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.currentGridImport')}</span>
              <span className="font-semibold text-blue-600">{gridData.importPower.toFixed(1)} {t('units.kW')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.currentGridExport')}</span>
              <span className="font-semibold text-green-600">{gridData.exportPower.toFixed(1)} {t('units.kW')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.currentNetPowerFlow')}</span>
              <span className={`font-semibold ${gridData.netPower > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {gridData.netPower > 0 ? '+' : ''}{gridData.netPower.toFixed(1)} {t('units.kW')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.totalEnergyImported')}</span>
              <span className="font-semibold">{gridData.importEnergy.toFixed(1)} {t('units.kWh')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('energy.totalEnergyExported')}</span>
              <span className="font-semibold">{gridData.exportEnergy.toFixed(1)} {t('units.kWh')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Room Temperatures */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🌡️ {t('energy.roomTemperatures')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.roomTemperatures.map((room, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{room.temperature.toFixed(1)}{t('units.celsius')}</div>
              <div className="text-sm text-gray-600">{room.room}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 {t('energy.performanceMetrics')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{data.pvProductionToday}</div>
            <div className="text-sm text-gray-600">{t('legends.pvProduction')} ({t('units.kWh')})</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{selfSufficiency.toFixed(1)}{t('units.percent')}</div>
            <div className="text-sm text-gray-600">{t('energy.selfSufficiency')}</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{(data.pvProductionToday / data.consumptionToday * 100).toFixed(1)}{t('units.percent')}</div>
            <div className="text-sm text-gray-600">{t('energy.pvCoverage')}</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{(data.consumption7Days / 7).toFixed(1)}</div>
            <div className="text-sm text-gray-600">{t('energy.avgDailyUse')}</div>
          </div>
        </div>
      </div>

    </div>
  );
};
